import React, { useEffect, useState, createElement, Component } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, CheckCircle2, ChevronDown, Clock, Zap, Brain, Battery, ShieldCheck, Star, ArrowRight } from 'lucide-react';
// --- Components ---
const FadeInSection = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-0">
    {children}
  </motion.div>;
const CHECKOUT_URL = 'https://www.metodo333.com/cart/52449087979796:1';
const META_PIXEL_ID = '1534608111621678';
// Declare fbq on window for TypeScript
declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    _fbq?: any;
  }
}
const trackAddToCart = () => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'AddToCart', {
      content_name: 'Dopamina Zero Bundle',
      content_ids: ['52449087979796'],
      content_type: 'product',
      value: 6.36,
      currency: 'USD'
    });
  }
};
const CTAButton = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <motion.a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" onClick={trackAddToCart} whileHover={{
  scale: 1.02,
  y: -2
}} whileTap={{
  scale: 0.98
}} className={`bg-cyan text-dark font-space font-bold text-lg md:text-xl px-8 py-5 rounded-lg shadow-[0_0_25px_rgba(0,212,255,0.35)] hover:shadow-[0_0_40px_rgba(0,212,255,0.6)] transition-all duration-300 flex items-center justify-center gap-3 w-full md:w-auto cursor-pointer no-underline ${className}`} data-id="element-1">
    {children}
    <ArrowRight className="w-6 h-6" data-id="element-2" />
  </motion.a>;
const Divider = () => <div className="w-full flex justify-center py-16 md:py-24" data-id="element-3">
    <div className="h-px w-3/4 md:w-1/2 bg-gradient-to-r from-transparent via-cyan/20 to-transparent" data-id="element-4" />
  </div>;
// --- Main App ---
export function App() {
  // Meta Pixel: Initialize and fire PageView on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Avoid double-injecting
    if (window.fbq) {
      window.fbq('track', 'PageView');
      return;
    }
    const f: any = window;
    const n: any = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      ;
      (document.head || document.body || document.documentElement).appendChild(script);
    }
    window.fbq?.('init', META_PIXEL_ID);
    window.fbq?.('track', 'PageView');
  }, []);
  // Countdown Timer Logic (11h 59m)
  const [timeLeft, setTimeLeft] = useState(11 * 3600 + 59 * 60);
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor(timeLeft % 3600 / 60);
  const seconds = timeLeft % 60;
  const formatTime = (num: number) => num.toString().padStart(2, '0');
  // FAQ State
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const faqs = [{
    q: '¿Tengo que dejar de usar redes sociales para siempre?',
    a: 'No. El protocolo te enseña a usarlas bajo tus reglas. Aprenderás a "ganarte" el tiempo de ocio sin que afecte tu química cerebral.'
  }, {
    q: '¿Cuánto tiempo al día requiere?',
    a: 'Menos de 15 minutos en ajustes físicos. El resto es simplemente seguir el nuevo diseño de tu día que ya hemos estructurado por ti.'
  }, {
    q: '¿Es seguro usar los suplementos recomendados?',
    a: 'Son aminoácidos y minerales naturales que encuentras en cualquier tienda de salud. No son fármacos.'
  }];
  const scrollToOffer = () => {
    document.getElementById('offer')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-dark text-gray-300 font-inter selection:bg-cyan/30 selection:text-white overflow-x-hidden" data-id="element-5">
      {/* 1. STICKY URGENCY BAR */}
      <div className="fixed top-0 left-0 w-full z-50 bg-amber/10 border-b border-amber/20 backdrop-blur-md" data-id="element-6">
        <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col md:flex-row items-center justify-center gap-2 md:gap-6 text-sm md:text-base" data-id="element-7">
          <div className="flex items-center gap-2 text-amber font-medium text-center" data-id="element-8">
            <div className="w-2 h-2 rounded-full bg-amber animate-pulse-fast shadow-[0_0_8px_rgba(255,176,0,0.8)]" data-id="element-9" />
            <span className="font-space tracking-wide" data-id="element-10">
              ⚠️ ATENCIÓN: El precio de lanzamiento con el 85% de descuento
              expira hoy a las 11:59 PM. Quedan{' '}
              <span className="font-bold text-white bg-amber/20 px-1 rounded" data-id="element-11">
                [14]
              </span>{' '}
              licencias disponibles a este precio.
            </span>
          </div>
          <div className="flex items-center gap-1 font-space font-bold text-white bg-dark-card px-3 py-1 rounded border border-amber/30" data-id="element-12">
            <Clock className="w-4 h-4 text-amber mr-1" data-id="element-13" />
            <span className="w-6 text-center" data-id="element-14">{formatTime(hours)}</span>:
            <span className="w-6 text-center" data-id="element-15">{formatTime(minutes)}</span>:
            <span className="w-6 text-center text-amber" data-id="element-16">
              {formatTime(seconds)}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content Wrapper (padding for sticky header) */}
      <main className="pt-28 md:pt-24 pb-20" data-id="element-17">
        {/* 2. HERO SECTION */}
        <section className="max-w-6xl mx-auto px-4 pt-10 md:pt-20" data-id="element-18">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-19">
            <FadeInSection className="order-2 lg:order-1" data-id="element-20">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan/10 border border-cyan/20 text-cyan text-sm font-medium mb-6" data-id="element-21">
                <Zap className="w-4 h-4" data-id="element-22" />
                <span data-id="element-23">Protocolo Científico de 21 Días</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-space font-bold text-white leading-[1.1] mb-6" data-id="element-24">
                Cómo Recuperar{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan to-blue-400" data-id="element-25">
                  4 Horas de Enfoque Profundo
                </span>{' '}
                al Día y Eliminar la "Niebla Mental" en Solo 21 Días Sin
                Abandonar la Tecnología.
              </h1>
              <p className="text-lg md:text-xl text-gray-400 mb-8 leading-relaxed" data-id="element-26">
                <span className="text-white font-medium" data-id="element-27">
                  — Incluso Si Hoy te Sientes Esclavo del Scroll Infinito y Tu
                  Productividad Está en Cero.
                </span>
                <br data-id="element-28" />
                <br data-id="element-29" />
                Deja de culpar a tu falta de voluntad. Es tu fisiología la que
                está rota. Aprende el protocolo científico para "resetear" tus
                receptores de dopamina y volver a sentir la chispa de la
                ambición.
              </p>
              <CTAButton data-id="element-30">SÍ, QUIERO RECUPERAR MI ENFOQUE</CTAButton>
            </FadeInSection>

            <FadeInSection delay={0.2} className="order-1 lg:order-2 relative" data-id="element-31">
              <div className="absolute inset-0 bg-cyan/20 blur-[100px] rounded-full" data-id="element-32" />
              <img src="https://cdn.magicpatterns.com/uploads/x1U2YW7xQFvbjtoePM5hT5/heylloyd-image-1777904934763.png" alt="Dopamina Zero Bundle" className="relative z-10 w-full h-auto rounded-2xl shadow-2xl border border-white/10 object-cover" data-id="element-33" />
            </FadeInSection>
          </div>
        </section>

        <Divider data-id="element-34" />

        {/* 3. SECCIÓN 1: EL GANCHO */}
        <section className="max-w-3xl mx-auto px-4 text-center" data-id="element-35">
          <FadeInSection data-id="element-36">
            <Battery className="w-12 h-12 text-red-400 mx-auto mb-6 opacity-80" data-id="element-37" />
            <h2 className="text-3xl md:text-4xl font-space font-bold text-white mb-6" data-id="element-38">
              ¿Te has fijado en cómo te sientes a las 11:00 AM?
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed" data-id="element-39">
              Tienes 15 pestañas abiertas, el teléfono ha vibrado 20 veces y,
              aunque el café aún está caliente, sientes que tu cerebro está
              "fundido". Sabes lo que tienes que hacer, pero simplemente... no
              puedes empezar.{' '}
              <strong className="text-white font-medium" data-id="element-40">
                No eres perezoso. Estás secuestrado por la dopamina barata.
              </strong>
            </p>
          </FadeInSection>
        </section>

        <Divider data-id="element-41" />

        {/* 4. SECCIÓN 2: AGITACIÓN DEL DOLOR */}
        <section className="max-w-4xl mx-auto px-4" data-id="element-42">
          <FadeInSection data-id="element-43">
            <div className="bg-dark-card border border-white/5 rounded-2xl p-8 md:p-12 relative overflow-hidden" data-id="element-44">
              <div className="absolute top-0 right-0 w-64 h-64 bg-red-500/5 blur-[80px] rounded-full" data-id="element-45" />

              <h2 className="text-2xl md:text-3xl font-space font-bold text-white mb-8 text-center" data-id="element-46">
                Vives en un estado de estimulación constante, pero de logro
                nulo.
              </h2>

              <div className="space-y-6 text-gray-400 text-lg leading-relaxed" data-id="element-47">
                <p data-id="element-48">
                  Te despiertas y lo primero que haces es recibir una descarga
                  de cortisol revisando notificaciones. Para el mediodía, tu
                  capacidad de concentración ha muerto.
                </p>
                <p data-id="element-49">
                  El costo no es solo el tiempo perdido. Es la sensación de que
                  tu potencial se te escapa de las manos. Es ver cómo otros, con
                  menos talento pero más enfoque, avanzan mientras tú saltas de
                  una app a otra buscando una dosis de placer instantáneo que
                  solo te deja más vacío y más cansado.
                </p>
                <div className="p-6 bg-red-500/10 border border-red-500/20 rounded-xl text-red-200 font-medium mt-8" data-id="element-50">
                  <AlertTriangle className="w-6 h-6 text-red-400 mb-3 inline-block mr-2" data-id="element-51" />
                  Si no intervienes hoy, tu cerebro de 2026 simplemente olvidará
                  cómo hacer el trabajo profundo que genera dinero.
                </div>
              </div>
            </div>
          </FadeInSection>
        </section>

        <Divider data-id="element-52" />

        {/* 5. SECCIÓN 3: LA REVELACIÓN */}
        <section className="max-w-4xl mx-auto px-4" data-id="element-53">
          <FadeInSection data-id="element-54">
            <div className="text-center mb-12" data-id="element-55">
              <Brain className="w-12 h-12 text-cyan mx-auto mb-6" data-id="element-56" />
              <h2 className="text-3xl md:text-4xl font-space font-bold text-white mb-6" data-id="element-57">
                La ciencia de 2026 es clara:
              </h2>
              <p className="text-xl text-cyan font-medium" data-id="element-58">
                No puedes curar una saturación química con "pensamientos
                positivos". Necesitas un Reset Fisiológico.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8" data-id="element-59">
              <div className="bg-dark-lighter p-8 rounded-2xl border border-white/5" data-id="element-60">
                <h3 className="text-white font-space font-bold text-xl mb-4 flex items-center gap-2" data-id="element-61">
                  <span className="text-red-400 text-2xl" data-id="element-62">✕</span> El Mito
                </h3>
                <p className="text-gray-400" data-id="element-63">
                  Creer que la clave es dejar internet por completo, aislarte
                  del mundo y depender únicamente de una fuerza de voluntad
                  inagotable que termina agotándote más.
                </p>
              </div>
              <div className="bg-cyan/5 p-8 rounded-2xl border border-cyan/20" data-id="element-64">
                <h3 className="text-white font-space font-bold text-xl mb-4 flex items-center gap-2" data-id="element-65">
                  <span className="text-cyan text-2xl" data-id="element-66">✓</span> La Solución
                </h3>
                <p className="text-gray-300" data-id="element-67">
                  Re-calibrar la sensibilidad de tus receptores. Cuando tus
                  receptores de dopamina están sanos, el trabajo difícil se
                  vuelve placentero de nuevo.
                </p>
              </div>
            </div>

            <p className="text-center text-lg text-gray-400 mt-10 max-w-2xl mx-auto" data-id="element-68">
              No necesitas más disciplina, necesitas una{' '}
              <strong className="text-white" data-id="element-69">Arquitectura de Dopamina</strong>{' '}
              que trabaje para ti, no en tu contra.
            </p>
          </FadeInSection>
        </section>

        <Divider data-id="element-70" />

        {/* 6. SECCIÓN 4: PRUEBA SOCIAL */}
        <section className="max-w-5xl mx-auto px-4" data-id="element-71">
          <FadeInSection data-id="element-72">
            <h2 className="text-3xl md:text-4xl font-space font-bold text-white mb-12 text-center" data-id="element-73">
              Resultados Reales. Enfoque Real.
            </h2>

            <div className="grid md:grid-cols-2 gap-6" data-id="element-74">
              <div className="bg-dark-card p-8 rounded-2xl border border-white/5 relative" data-id="element-75">
                <div className="flex text-amber mb-4" data-id="element-76">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" data-id="element-77" />)}
                </div>
                <p className="text-gray-300 italic mb-6 text-lg" data-id="element-78">
                  "Pasé de procrastinar 6 horas al día a terminar mi reporte
                  semanal en solo una mañana. La niebla mental desapareció al
                  cuarto día."
                </p>
                <div className="flex items-center justify-between border-t border-white/10 pt-4" data-id="element-79">
                  <div data-id="element-80">
                    <p className="text-white font-bold font-space" data-id="element-81">Carlos R.</p>
                    <p className="text-sm text-gray-500" data-id="element-82">
                      Desarrollador de Software
                    </p>
                  </div>
                  <div className="bg-cyan/10 text-cyan text-sm font-bold px-3 py-1 rounded-full" data-id="element-83">
                    Resultado: +3h extra/día
                  </div>
                </div>
              </div>

              <div className="bg-dark-card p-8 rounded-2xl border border-white/5 relative" data-id="element-84">
                <div className="flex text-amber mb-4" data-id="element-85">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" data-id="element-86" />)}
                </div>
                <p className="text-gray-300 italic mb-6 text-lg" data-id="element-87">
                  "Como madre y emprendedora, mi cerebro estaba siempre al
                  límite. Este protocolo me devolvió la calma y la capacidad de
                  estar presente."
                </p>
                <div className="flex items-center justify-between border-t border-white/10 pt-4" data-id="element-88">
                  <div data-id="element-89">
                    <p className="text-white font-bold font-space" data-id="element-90">
                      Mariana L.
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-91">
                      Fundadora de Agencia
                    </p>
                  </div>
                  <div className="bg-cyan/10 text-cyan text-sm font-bold px-3 py-1 rounded-full" data-id="element-92">
                    Ansiedad: -70%
                  </div>
                </div>
              </div>
            </div>
          </FadeInSection>
        </section>

        <Divider data-id="element-93" />

        {/* 7. SECCIÓN 5 & 6: PRESENTACIÓN DE LA OFERTA Y PRECIO */}
        <section id="offer" className="max-w-5xl mx-auto px-4 scroll-mt-32" data-id="element-94">
          <FadeInSection data-id="element-95">
            <div className="text-center mb-12" data-id="element-96">
              <h2 className="text-3xl md:text-5xl font-space font-bold text-white mb-4" data-id="element-97">
                Presentamos: EL BUNDLE{' '}
                <span className="text-cyan" data-id="element-98">"DOPAMINA ZERO"</span>
              </h2>
              <p className="text-xl text-gray-400" data-id="element-99">
                El sistema completo para dueños de negocios y profesionales que
                quieren dominar su atención.
              </p>
            </div>

            <div className="grid lg:grid-cols-12 gap-8 items-start" data-id="element-100">
              {/* Bundle Image */}
              <div className="lg:col-span-5 relative" data-id="element-101">
                <div className="absolute inset-0 bg-cyan/20 blur-[80px] rounded-full" data-id="element-102" />
                <img src="https://cdn.magicpatterns.com/uploads/x1U2YW7xQFvbjtoePM5hT5/heylloyd-image-1777904934763.png" alt="Bundle Dopamina Zero" className="relative z-10 w-full rounded-2xl shadow-2xl border border-white/10" data-id="element-103" />
              </div>

              {/* Offer Details & Pricing */}
              <div className="lg:col-span-7 bg-dark-card border border-cyan/30 rounded-3xl p-8 md:p-10 shadow-[0_0_50px_rgba(0,212,255,0.1)] relative overflow-hidden" data-id="element-104">
                <div className="absolute top-0 right-0 bg-amber text-dark font-bold font-space px-6 py-1 rounded-bl-xl text-sm" data-id="element-105">
                  85% DESCUENTO HOY
                </div>

                <h3 className="text-2xl font-space font-bold text-white mb-6 border-b border-white/10 pb-4" data-id="element-106">
                  Lo que obtienes hoy:
                </h3>

                <ul className="space-y-4 mb-8" data-id="element-107">
                  <li className="flex items-start gap-3" data-id="element-108">
                    <CheckCircle2 className="w-6 h-6 text-cyan shrink-0 mt-0.5" data-id="element-109" />
                    <div data-id="element-110">
                      <p className="text-white font-medium" data-id="element-111">
                        <strong className="text-cyan" data-id="element-112">Ebook Principal:</strong>{' '}
                        Dopamina Zero (60 pág)
                      </p>
                      <p className="text-sm text-gray-400" data-id="element-113">
                        El protocolo paso a paso de 21 días para sanar tu
                        biología.{' '}
                        <span className="line-through text-gray-500 ml-1" data-id="element-114">
                          ($197 Valor)
                        </span>
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-115">
                    <CheckCircle2 className="w-6 h-6 text-cyan shrink-0 mt-0.5" data-id="element-116" />
                    <div data-id="element-117">
                      <p className="text-white font-medium" data-id="element-118">
                        <strong className="text-cyan" data-id="element-119">Bonus 1:</strong>{' '}
                        Masterclass IA para Enfoque
                      </p>
                      <p className="text-sm text-gray-400" data-id="element-120">
                        Configura tus dispositivos para que sean herramientas,
                        no trampas.{' '}
                        <span className="line-through text-gray-500 ml-1" data-id="element-121">
                          ($97 Valor)
                        </span>
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-122">
                    <CheckCircle2 className="w-6 h-6 text-cyan shrink-0 mt-0.5" data-id="element-123" />
                    <div data-id="element-124">
                      <p className="text-white font-medium" data-id="element-125">
                        <strong className="text-cyan" data-id="element-126">Bonus 2:</strong> Guía de
                        Suplementación
                      </p>
                      <p className="text-sm text-gray-400" data-id="element-127">
                        Los nutrientes exactos que aceleran la reparación de tus
                        neuronas.{' '}
                        <span className="line-through text-gray-500 ml-1" data-id="element-128">
                          ($67 Valor)
                        </span>
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-129">
                    <CheckCircle2 className="w-6 h-6 text-cyan shrink-0 mt-0.5" data-id="element-130" />
                    <div data-id="element-131">
                      <p className="text-white font-medium" data-id="element-132">
                        <strong className="text-cyan" data-id="element-133">Bonus 3:</strong> Planner
                        Modo Monje
                      </p>
                      <p className="text-sm text-gray-400" data-id="element-134">
                        Tu mapa diario para eliminar la fatiga de decisión.{' '}
                        <span className="line-through text-gray-500 ml-1" data-id="element-135">
                          ($47 Valor)
                        </span>
                      </p>
                    </div>
                  </li>
                </ul>

                <div className="bg-dark-lighter rounded-xl p-6 mb-8 border border-white/5" data-id="element-136">
                  <p className="text-gray-400 text-sm mb-4" data-id="element-137">
                    Si contrataras a un experto en Biohacking para este nivel de
                    optimización, pagarías no menos de $500 USD. Pero hoy, como
                    parte de mi misión de rescatar la atención de LATAM, no
                    pagarás eso. Ni siquiera pagarás $97.
                  </p>
                  <div className="flex items-end gap-4 mb-2" data-id="element-138">
                    <span className="text-gray-500 line-through text-2xl font-space" data-id="element-139">
                      $408 USD
                    </span>
                    <span className="text-5xl font-space font-bold text-white" data-id="element-140">
                      $6.36 <span className="text-2xl text-cyan" data-id="element-141">USD</span>
                    </span>
                  </div>
                  <p className="text-amber text-sm font-medium" data-id="element-142">
                    Este precio es una prueba de mercado y subirá a su valor
                    real muy pronto.
                  </p>
                </div>

                <CTAButton className="w-full py-6 text-xl" data-id="element-143">
                  ACCESO INMEDIATO POR $6.36
                </CTAButton>
                <div className="flex items-center justify-center gap-2 mt-4 text-sm text-gray-500" data-id="element-144">
                  <ShieldCheck className="w-4 h-4" data-id="element-145" /> Pago 100% Seguro y
                  Encriptado
                </div>
              </div>
            </div>
          </FadeInSection>
        </section>

        <Divider data-id="element-146" />

        {/* 8. SECCIÓN 7: FAQ */}
        <section className="max-w-3xl mx-auto px-4" data-id="element-147">
          <FadeInSection data-id="element-148">
            <h2 className="text-3xl font-space font-bold text-white mb-8 text-center" data-id="element-149">
              Preguntas Frecuentes
            </h2>
            <div className="space-y-4" data-id="element-150">
              {faqs.map((faq, index) => <div key={index} className="bg-dark-card border border-white/10 rounded-xl overflow-hidden transition-all duration-300" data-id="element-151">
                  <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full px-6 py-5 text-left flex items-center justify-between focus:outline-none" data-id="element-152">
                    <span className="font-space font-bold text-white text-lg pr-8" data-id="element-153">
                      {faq.q}
                    </span>
                    <ChevronDown className={`w-5 h-5 text-cyan transition-transform duration-300 shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} data-id="element-154" />
                  </button>
                  <div className={`px-6 overflow-hidden transition-all duration-300 ease-in-out ${openFaq === index ? 'max-h-48 pb-5 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-155">
                    <p className="text-gray-400" data-id="element-156">{faq.a}</p>
                  </div>
                </div>)}
            </div>
          </FadeInSection>
        </section>

        <Divider data-id="element-157" />

        {/* 9. SECCIÓN 8: LA GARANTÍA */}
        <section className="max-w-4xl mx-auto px-4" data-id="element-158">
          <FadeInSection data-id="element-159">
            <div className="bg-gradient-to-br from-dark-card to-dark border border-cyan/20 rounded-3xl p-8 md:p-12 text-center relative overflow-hidden" data-id="element-160">
              <ShieldCheck className="w-16 h-16 text-cyan mx-auto mb-6" data-id="element-161" />
              <h2 className="text-2xl md:text-3xl font-space font-bold text-white mb-6" data-id="element-162">
                La Garantía de "Cero Niebla Mental" de 7 Días
              </h2>
              <p className="text-lg text-gray-400 leading-relaxed max-w-2xl mx-auto" data-id="element-163">
                Si en una semana no sientes una claridad mental superior a
                cualquier cosa que hayas sentido en el último año, envíanos un
                email. Te devolvemos cada centavo. Sin preguntas.{' '}
                <strong className="text-white" data-id="element-164">
                  El riesgo de probarlo es mío, el riesgo de no hacerlo es que
                  sigas perdiendo tu tiempo para siempre.
                </strong>
              </p>
            </div>
          </FadeInSection>
        </section>

        <Divider data-id="element-165" />

        {/* 10. SECCIÓN 9: CIERRE DE ALTA CONVERSIÓN */}
        <section className="max-w-3xl mx-auto px-4 text-center pb-12" data-id="element-166">
          <FadeInSection data-id="element-167">
            <h2 className="text-3xl md:text-4xl font-space font-bold text-white mb-8" data-id="element-168">
              Tienes dos caminos hoy.
            </h2>

            <div className="grid md:grid-cols-2 gap-6 mb-12 text-left" data-id="element-169">
              <div className="bg-dark-card border border-red-500/20 p-6 rounded-2xl" data-id="element-170">
                <h3 className="text-red-400 font-space font-bold text-xl mb-3" data-id="element-171">
                  Camino A:
                </h3>
                <p className="text-gray-400" data-id="element-172">
                  Cierras esta página y sigues con el scroll. En 6 meses, la
                  niebla será más densa y habrás perdido cientos de horas de tu
                  vida.
                </p>
              </div>
              <div className="bg-cyan/10 border border-cyan/30 p-6 rounded-2xl" data-id="element-173">
                <h3 className="text-cyan font-space font-bold text-xl mb-3" data-id="element-174">
                  Camino B:
                </h3>
                <p className="text-gray-300" data-id="element-175">
                  Inviertes menos de lo que cuesta una cena fuera y recuperas el
                  control de tu recurso más valioso:{' '}
                  <strong className="text-white" data-id="element-176">Tu Atención.</strong>
                </p>
              </div>
            </div>

            <CTAButton className="mx-auto py-6 text-xl px-10" data-id="element-177">
              SÍ, QUIERO RECUPERAR MI ENFOQUE — $6.36
            </CTAButton>
            <p className="mt-6 text-sm text-gray-500" data-id="element-178">
              Acceso inmediato. Pago único. Sin suscripciones ocultas.
            </p>
          </FadeInSection>
        </section>
      </main>
    </div>;
}